import { trackAnalyticsEvent } from '@eventbrite/site-analytics';
import { Wrapper } from '@googlemaps/react-wrapper';
import React, { Attributes } from 'react';
import { GACodes, Location } from '../../../../types';
import { EVENT_LOCATION_MAP_ID } from '../../../_shared/constants';
import './Map.scss';
import { mapOptions, markerOptions } from './MapStyles';

export type MapProps = {
    location: Location;
    mapZoom: number;
    gMapsApiKey: string;
};

interface GMapProps extends google.maps.MapOptions {
    style: { [key: string]: string };
}

const GMap: React.FC<GMapProps> = ({ children, style, ...options }) => {
    const ref = React.useRef<HTMLDivElement>(null);
    const [map, setMap] = React.useState<google.maps.Map>();

    React.useEffect(() => {
        if (ref.current && !map) {
            const googleMap = new window.google.maps.Map(ref.current, {});
            setMap(googleMap);
        }
    }, [ref, map]);

    React.useEffect(() => {
        if (map) {
            map.setOptions(options);
        }
    }, [map, options]);

    React.useEffect(() => {
        if (map) {
            map.addListener('zoom_changed', () => {
                trackAnalyticsEvent({
                    category: GACodes.GA_CATEGORY_LISTING,
                    action: GACodes.GA_ACTION_MAP_INTERACTION,
                    label: GACodes.GA_LABEL_ZOOM,
                });
            });
        }
    }, [map]);

    const cloneChildrenAddingMapProp = () =>
        React.Children.map(children, (child) => {
            if (React.isValidElement(child)) {
                return React.cloneElement(child, { map } as Attributes);
            }
        });

    return (
        <>
            <div ref={ref} style={style} aria-hidden="true"></div>
            {cloneChildrenAddingMapProp()}
        </>
    );
};

export const Marker: React.FC<google.maps.MarkerOptions> = (options) => {
    const [marker, setMarker] = React.useState<google.maps.Marker>();

    React.useEffect(() => {
        if (!marker) {
            setMarker(new google.maps.Marker());
        }
    }, [marker]);

    React.useEffect(() => {
        if (marker) {
            marker.setOptions({ ...options });
        }
    }, [marker, options]);

    return null;
};

const MapContainer = ({
    mapZoom,
    location,
    mapOptions,
    markerOptions,
    api_key,
}: {
    mapZoom: number;
    location: Location;
    mapOptions: google.maps.MapOptions;
    markerOptions: google.maps.MarkerOptions;
    api_key: string;
}) => {
    const center: google.maps.LatLngLiteral = {
        lat: location.latitude,
        lng: location.longitude,
    };
    return (
        <Wrapper apiKey={api_key}>
            <GMap
                center={center}
                zoom={mapZoom}
                style={{ width: '100%', height: '100%' }}
                {...mapOptions}
            >
                <Marker
                    position={{
                        lat: location.latitude,
                        lng: location.longitude,
                    }}
                    {...markerOptions}
                />
            </GMap>
        </Wrapper>
    );
};

export const Map: React.FunctionComponent<MapProps> = ({
    location,
    mapZoom,
    gMapsApiKey,
}) => {
    return (
        <>
            <div
                id={EVENT_LOCATION_MAP_ID}
                className="eds-fx--fade-in"
                role="region"
                data-testid={EVENT_LOCATION_MAP_ID}
                tabIndex={-1}
            >
                <div className="map-wrapper">
                    <div className="map-wrapper__inner">
                        <MapContainer
                            mapZoom={mapZoom}
                            location={location}
                            mapOptions={mapOptions}
                            markerOptions={markerOptions}
                            api_key={gMapsApiKey}
                        />
                    </div>
                </div>
            </div>
        </>
    );
};
